import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { type MarkConsultantRequest, type SetExpressLinkCodeRequest } from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';

/** Every Consultant, with the campaigns their Express link created. */
export function useConsultants(enabled = true) {
  return useQuery({
    queryKey: queryKeys.consultants.list(),
    queryFn: () => getApiClient().admin.consultants.list(),
    enabled,
  });
}

/** Users matching a name or email; nothing is asked until there is a query. */
export function useConsultantUserSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.consultants.search(query),
    queryFn: () => getApiClient().admin.consultants.search(query),
    enabled: query.trim() !== '',
  });
}

/**
 * Mark or unmark a Consultant against the user's concurrency stamp. Success or a stale stamp re-asks
 * every Consultant read, so the list, the search and the next stamp are all fresh.
 */
export function useSetConsultant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: MarkConsultantRequest }) =>
      getApiClient().admin.consultants.set(userId, request),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.consultants.all }),
  });
}

/**
 * Replace a Consultant's Express link ending against their concurrency stamp. The old ending stops working.
 * Success or a refusal re-asks every Consultant read, so the row shows the stored link and the next stamp.
 */
export function useSetExpressLinkCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: SetExpressLinkCodeRequest }) =>
      getApiClient().admin.consultants.setLinkCode(userId, request),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.consultants.all }),
  });
}
